import { Button, Input, useToastMessage } from 'ccs-ui-components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useLoader from '../../../../../common/hooks/Loader';
import { createScheduler } from '../../../../store/_api';
import { regex } from '../../../../constants/JdRegex';
import EventPayload from '../event-payload/EventPayload';
import { JobScheduler } from '../job-scheduler/JobScheduler';
import { DailyComp } from '../schedule-view/DailyComp';
import { HourlyComp } from '../schedule-view/HourlyComp';
import { MonthlyTxtComp } from '../schedule-view/MonthlyComp';
import { WeeklyCompTxt } from '../schedule-view/WeekComp';
import { createScheduledConstants, ICreateScheduleProps, IJobSchedulerRequest, ISchedulerCronDetails, jobValidation, typeFrequency } from '../../utilities/TypeDefs';
import { dateToUtcConversion, getUtcDate, isJSONHandler, maxScheduleVal, monthDaysTxtHandler, weekDaysTxtHandler } from '../../utilities/Utility';
import classes from './CreateSchedule.module.scss';

export const CreateSchedule = ({ setCancel, journeyName, journeyStepName, eventName, eventVersion, setApiSuccess, journeyVersion }: ICreateScheduleProps) => {
  // state variables
  const [jobName, setJobName] = useState('');
  const [eventPayload, setEventPayload] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(new Date());
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<undefined | string>(undefined);
  const [startHour, setStartHour] = useState<string | number>('12');
  const [startMinutes, setStartMinutes] = useState<string | number>('00');
  const [endHour, setEndHour] = useState<string | number>('12');
  const [endMinutes, setEndMinutes] = useState<string | number>('00');
  const [frequency, setFrequency] = useState<number>(0);
  const [invalidJobName, setInvalidJobName] = useState(jobValidation.VALID);
  const [isValidEventPayload, setIsValidEventPayload] = useState<boolean>(true);
  const [eventPayloadRequired, setEventPayloadRequired] = useState<boolean>(false);
  const [eventPayloadChanged, setEventPayloadChanged] = useState<boolean>(false);
  const [days, setDays] = useState<string[]>([]);
  const [daysOfMonth, setDaysOfMonth] = useState<number>(0);
  const [weeksList, setWeeksList] = useState<string | number>('1');
  const [daysList, setDaysList] = useState<string>('Monday');
  const [monthRadio, setMonthRadio] = useState<boolean>(true);

  // hooks
  const toastMessage = useToastMessage();
  const loader = useLoader();
  const { t: translate } = useTranslation();

  // local variables
  let frequencyText = '';

  /**
   * @method handleStartHourChange
   * @description handles and sets the start hour for creating the schedule
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const handleStartHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStartHour(e.target.value);
  };

  /**
  * @method handleStartMinuteChange
  * @description handles and sets the start minutes for creating the schedule
  * @param {React.ChangeEvent<HTMLSelectElement>} e
  */
  const handleStartMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStartMinutes(e.target.value);
  };

  /**
   * @method handleEndHourChange
   * @description handles and sets the end hour for creating the schedule
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const handleEndHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEndHour(e.target.value);
  };

  /**
   * @method handleEndMinuteChange
   * @description handles and sets the end minutes for creating the schedule
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const handleEndMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEndMinutes(e.target.value);
  };

  /**
   * @method handleWeeksListsChange
   * @description handles and sets the list of weeks for creating the schedule
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const handleWeeksListsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setWeeksList(e.target.value);
  };

  /**
  * @method handleDaysListsChange
  * @description handles and sets the list of week days for creating the schedule
  * @param {React.ChangeEvent<HTMLSelectElement>} e
  */
  const handleDaysListsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDaysList(e.target.value);
  };

  /**
  * @method monthRadioHandler
  * @description checks for the month schedule sub-option (schedule by days or weekDays)
  */
  const monthRadioHandler = () => {
    setMonthRadio(!monthRadio);
  };

  let schedulerCronData: ISchedulerCronDetails = {
    frequencyType: '',
    frequency: 1,
    startsAtHour: 12,
    startsAtMinutes: 0,
    startsAtSeconds: 0,
    endsAtHour: 12,
    endsAtMinutes: 0,
    endsAtSeconds: 0,
    days: '',
    dayOfMonth: 1,
    dayOfWeek: '',
    weekOfMonth: 0,
    startDate: '',
    endDate: ''
  };

  /**
   * @method jobNameHandler
   * @description sets and handles the entered Job Name Validation
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const jobNameHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setInvalidJobName(jobValidation.EMPTY);
    } else if (e.target.value.length > createScheduledConstants.jobNameMaxLength) {
      setInvalidJobName(jobValidation.LENGTH_EXCEEDED);
      return;
    } else if (regex.name.test(e.target.value)) {
      setInvalidJobName(jobValidation.VALID);
    } else {
      setInvalidJobName(jobValidation.INVALID);
    }
    setJobName(e.target.value);
  };

  /**
   * @method payloadHandler
   * @description sets and handles the payload data
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const payloadHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEventPayloadChanged(true);
    if (e.target.value.length === 0 && eventPayloadChanged) {
      setIsValidEventPayload(true);
      setEventPayloadRequired(true);
    } else if (isJSONHandler(e.target.value) === false) {
      setIsValidEventPayload(false);
      setEventPayloadRequired(false);
    } else {
      setEventPayloadRequired(false);
      setIsValidEventPayload(true);
    }
    setEventPayload(e.target.value);
  };

  /**
   * @method onblurHandler
   * @description sets the input value to 1 on blur incase corrupt value is provided
   * @param {React.SyntheticEvent<HTMLInputElement>} e
   */
  const onblurHandler = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = parseInt(e.currentTarget.value, 10);
    if (value > 0 && value <= maxScheduleVal(selectedSchedule)) {
      setFrequency(value - 1);
    } else if (value <= 0 || e.currentTarget.value === '' || Number.isNaN(value)) {
      e.currentTarget.value = '1';
      setFrequency(0);
    } else {
      e.currentTarget.value = maxScheduleVal(selectedSchedule).toString();
      setFrequency(maxScheduleVal(selectedSchedule) - 1);
    }
  };

  /**
   * @method onkeyUpHandler
   * @description executes the onblurHandler function on enter
   * @param {React.KeyboardEvent<HTMLInputElement>} e
   */
  const onkeyUpHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onblurHandler(e);
    }
  };

  /**
  * @method onKeyDownHandler
  * @description prevents the miscellaneous numeric values to be entered
  * @param {React.KeyboardEvent<HTMLInputElement>} e
  */
  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const notValid = ['e', 'E', '+', '-', '.'];
    if (notValid.includes(e.key)) e.preventDefault();
  };

  /**
  * @method schedulerTypeChangeHandler
  * @description handles schedule type
  * @param {React.ChangeEvent<HTMLSelectElement>} e
  */
  const schedulerTypeChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSchedule(e.target.value);
    if (e.target.value !== typeFrequency.WEEKLY) {
      setDays([]);
    }
  };

  const monthRadioTxt1 = `${translate('month_component.txt_day')} ${(daysOfMonth + 1).toString()}`;

  const weekDaysTxt = translate(`weekDays.${daysList}`);
  const monthRadioTxt2 = `${monthDaysTxtHandler(weeksList.toString(), translate)} ${weekDaysTxt}`;

  /**
   * @method scheduleTxtHandler
   * @returns Schedule Text depending on the schedule selected
   */
  const scheduleTxtHandler = () => {
    switch (selectedSchedule) {
      case typeFrequency.HOURLY:
        schedulerCronData = {
          frequencyType: selectedSchedule,
          frequency: frequency + 1,
          startsAtHour: getUtcDate(fromDate, startHour, startMinutes).utcHour,
          startsAtMinutes: getUtcDate(fromDate, startHour, startMinutes).utcMinute,
          startsAtSeconds: 0,
          endsAtHour: getUtcDate(toDate, endHour, endMinutes).utcHour,
          endsAtMinutes: getUtcDate(toDate, endHour, endMinutes).utcMinute,
          endsAtSeconds: 0,
          startDate: getUtcDate(fromDate, startHour, startMinutes).utcDate,
          endDate: getUtcDate(toDate, endHour, endMinutes).utcDate
        };
        frequencyText = translate('job_scheduler_component.txt_hours_schedule', {
          replace: {
            freq: frequency + 1,
            from: moment(fromDate).format(String(translate('time-format'))),
            to: moment(toDate).format(String(translate('time-format')))
          }
        });
        return (<HourlyComp hourlyText={frequencyText} />);
      case typeFrequency.DAILY:
        schedulerCronData = {
          frequencyType: selectedSchedule,
          frequency: frequency + 1,
          startsAtHour: getUtcDate(fromDate, startHour, startMinutes).utcHour,
          startsAtMinutes: getUtcDate(fromDate, startHour, startMinutes).utcMinute,
          startsAtSeconds: 0,
          endsAtHour: getUtcDate(toDate, endHour, endMinutes).utcHour,
          endsAtMinutes: getUtcDate(toDate, endHour, endMinutes).utcMinute,
          endsAtSeconds: 0,
          startDate: getUtcDate(fromDate, startHour, startMinutes).utcDate,
          endDate: getUtcDate(toDate, endHour, endMinutes).utcDate
        };
        frequencyText = (translate('job_scheduler_component.txt_day_schedule', {
          replace: {
            dayFrequency: frequency + 1,
            from: moment(fromDate).format(String(translate('time-format'))),
            to: moment(toDate).format(String(translate('time-format')))
          }
        }));
        return (<DailyComp
          dailyText={frequencyText}
        />);
      case typeFrequency.WEEKLY:
        if (days.length) {
          schedulerCronData = {
            frequencyType: selectedSchedule,
            frequency: frequency + 1,
            startsAtHour: getUtcDate(fromDate, startHour, startMinutes).utcHour,
            startsAtMinutes: getUtcDate(fromDate, startHour, startMinutes).utcMinute,
            startsAtSeconds: 0,
            endsAtHour: getUtcDate(toDate, endHour, endMinutes).utcHour,
            endsAtMinutes: getUtcDate(toDate, endHour, endMinutes).utcMinute,
            endsAtSeconds: 0,
            startDate: getUtcDate(fromDate, startHour, startMinutes).utcDate,
            endDate: getUtcDate(toDate, endHour, endMinutes).utcDate,
            days: days.toString()
          };
          frequencyText = translate('job_scheduler_component.txt_week_schedule', {
            replace: {
              weekNumber: frequency + 1,
              days: weekDaysTxtHandler(days, translate),
              from: moment(fromDate).format(String(translate('time-format'))),
              to: moment(toDate).format(String(translate('time-format')))
            }
          });
          return (<WeeklyCompTxt weeklyText={frequencyText} />);
        } return null;
      case typeFrequency.MONTHLY:
        schedulerCronData = {
          frequencyType: selectedSchedule,
          frequency: frequency + 1,
          startsAtHour: getUtcDate(fromDate, startHour, startMinutes).utcHour,
          startsAtMinutes: getUtcDate(fromDate, startHour, startMinutes).utcMinute,
          startsAtSeconds: 0,
          endsAtHour: getUtcDate(toDate, endHour, endMinutes).utcHour,
          endsAtMinutes: getUtcDate(toDate, endHour, endMinutes).utcMinute,
          endsAtSeconds: 0,
          startDate: getUtcDate(fromDate, startHour, startMinutes).utcDate.toString(),
          endDate: getUtcDate(toDate, endHour, endMinutes).utcDate.toString(),
          dayOfMonth: monthRadio ? daysOfMonth + 1 : 0,
          dayOfWeek: daysList,
          weekOfMonth: +weeksList,
        };
        frequencyText = translate('job_scheduler_component.txt_month_schedule', {
          replace: {
            freq: frequency + 1,
            monthDay: `${monthRadio ? monthRadioTxt1 : monthRadioTxt2}`,
            from: moment(fromDate).format(String(translate('time-format'))),
            to: moment(toDate).format(String(translate('time-format')))
          }
        });
        return (<MonthlyTxtComp monthlyText={frequencyText} />);
      default:
        return null;
    }
  };

  /**
   * @method cancelSchedule
   * @description cancels the schedule
   */
  const cancelSchedule = () => {
    setCancel(false);
  };

  /**
   * @method createSchedule
   * @description saves the schedule after giving payload to createScheduler api function
   * @returns api saved schedule call
   */
  const createSchedule = async () => {
    if (dateToUtcConversion(fromDate?.getFullYear() ?? 0, fromDate?.getMonth() ?? 0, fromDate?.getDate() ?? 0, +startHour, +startMinutes) <= dateToUtcConversion((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate(), (new Date()).getHours(), (new Date()).getMinutes())) {
      setApiSuccess(false);
      toastMessage.failure(
        translate('job_scheduler_component.title_toast_error_header'),
        translate('job_scheduler_component.time_validation_txt1')
      );
      return;
    }
    if (fromDate?.toDateString() === toDate?.toDateString() && dateToUtcConversion(toDate?.getFullYear() ?? 0, toDate?.getMonth() ?? 0, toDate?.getDate() ?? 0, +endHour, +endMinutes) < dateToUtcConversion(fromDate?.getFullYear() ?? 0, fromDate?.getMonth() ?? 0, fromDate?.getDate() ?? 0, +startHour, +startMinutes)) {
      setApiSuccess(false);
      toastMessage.failure(
        translate('job_scheduler_component.title_toast_error_header'),
        translate('job_scheduler_component.time_validation_txt2')
      );
      return;
    }
    loader.show();
    const payload: IJobSchedulerRequest = {
      jobName,
      journeyName,
      journeyStepName,
      journeyVersion,
      createdBy: createScheduledConstants.created_by,
      eventName,
      eventVersion,
      frequency: frequencyText,
      eventPayload: JSON.parse(eventPayload),
      schedulerCronDetails: schedulerCronData
    };
    const response = await createScheduler(payload);
    loader.hide();
    if (response.isError && response.errorMessage) {
      setApiSuccess(false);
      toastMessage.failure(
        translate('job_scheduler_component.title_toast_error_header'),
        response.errorMessage
      );
    } else {
      setApiSuccess(true);
      toastMessage.success(
        translate('job_scheduler_component.title_toast_success_header'),
        translate('job_scheduler_component.api_successful')
      );
      setCancel(false);
    }
  };

  const errorText = () => {
    if (invalidJobName === jobValidation.EMPTY) {
      return String(
        translate('job_scheduler_component.txt_invalid_job_name', {
          replace: { variable: translate('step_elements.txt_required') }
        })
      );
    }
    if (invalidJobName === jobValidation.LENGTH_EXCEEDED) {
      return String(
        translate('job_scheduler_component.txt_job_name_length_exceed')
      );
    }
    if (invalidJobName === jobValidation.INVALID) {
      return String(translate('job_scheduler_component.txt_invalid_job_name', {
        replace: { variable: translate('step_elements.txt_invalid') }
      }));
    }
    return ('');
  };

  /**
   * @description disables the save schedule button
   */
  const isSaveButtonDisabled = (jobName === '' || invalidJobName === jobValidation.INVALID || invalidJobName === jobValidation.LENGTH_EXCEEDED || !fromDate || !toDate || !scheduleTxtHandler() || !isJSONHandler(eventPayload));

  useEffect(() => {
    if (frequency > maxScheduleVal(selectedSchedule)) {
      setFrequency(maxScheduleVal(selectedSchedule) - 1);
    }
  }, [frequency, selectedSchedule]);

  return (
    <>
      {loader.render()}
      {toastMessage.renderToast()}
      <hr className="m-0" />
      <div className={classes['create-schedule-container']}>
        <div className={classes['create-heading']}>{translate('job_scheduler_component.txt_create_journey_heading')}</div>
        <div className={classes['job-name']}>
          <div className={classes['job-name-input']}>
            <Input
              label={undefined}
              labelText={String(translate('job_scheduler_component.txt_job_name'))}
              helperText={String(translate('job_scheduler_component.txt_job_name_helper'))}
              isMandatory
              name=""
              placeholder={String(translate('job_scheduler_component.txt_job_name_Placeholder'))}
              value={jobName}
              onChange={jobNameHandler}
              error={invalidJobName !== jobValidation.VALID}
              errorTxt={errorText()}
              data-testid="jobInput"
              infoIconTxt={String(translate('job_scheduler_component.job_name_info_bubble'))}
            />
          </div>
        </div>
        <div className={classes['event-payload']}>
          <EventPayload value={eventPayload} onChange={payloadHandler} isValidEventPayload={isValidEventPayload} eventPayloadRequired={eventPayloadRequired} />
        </div>
        <div className={classes.schedule}>
          <JobScheduler
            startDate={fromDate}
            startDateHandler={(date) => setFromDate(date)}
            startHour={startHour}
            startMinute={startMinutes}
            startHourHandler={handleStartHourChange}
            startMinuteHandler={handleStartMinuteChange}
            blurHandler={onblurHandler}
            keyUpHandler={onkeyUpHandler}
            keydownHandler={onKeyDownHandler}
            repetition={frequency}
            schedule={selectedSchedule}
            scheduleHandler={schedulerTypeChangeHandler}
            endDate={toDate}
            endDateHandler={(date) => setToDate(date)}
            endHour={endHour}
            endMinute={endMinutes}
            endHourHandler={handleEndHourChange}
            endMinuteHandler={handleEndMinuteChange}
            weekDays={days}
            setWeekDays={setDays}
            monthDay={daysOfMonth}
            monthDayHandler={(daysOfMonth) => setDaysOfMonth(daysOfMonth)}
            listWeek={weeksList}
            listDays={daysList}
            listWeekHandler={handleWeeksListsChange}
            listDaysHandler={handleDaysListsChange}
            monthRadio={monthRadio}
            monthRadioHandler={monthRadioHandler}
          />
        </div>
        {!isSaveButtonDisabled && (
          <div data-testid="scheduleText">
            {scheduleTxtHandler()}
          </div>)}
        <div className={`${classes['create-btn']} ci-button-group`}>
          <Button variant="secondary" className={classes.cancel} onClick={cancelSchedule} data-testid="cancelBtn">{translate('job_scheduler_component.btn_txt_cancel')}</Button>
          <Button variant="primary" className={classes.save} onClick={createSchedule} disabled={isSaveButtonDisabled} data-testid="saveBtn">{translate('job_scheduler_component.btn_txt_save')}</Button>
        </div>
      </div>
    </>
  );
};
